import {
	Component,
	ElementRef,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChildren,
	ViewEncapsulation
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription, take } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { FilterType } from '../../models/datacenter-filters';
import { pageTransitions } from '../../animations/page-transitions';
import {
	DocumentLastConnectionDateComponent
} from '../types/commercial-documents/components/document-last-connection-date-filter.component';
import { DocumentMillDropdownFilterComponent }
	from '../types/commercial-documents/components/document-mill-dropdown-filter.component';
import {
	DocumentOrderNumberFilterComponent
} from '../types/commercial-documents/components/document-order-number-filter.component';
import { DocumentDateFilterComponent } from '../types/commercial-documents/components/document-date-filter.component';
import {
	ShipmentOrderNumberFilterComponent
} from '../types/shipments/components/shipment-order-number-filter.component';
import {
	DocumentSuggestiveListIdFilterComponent
} from '../types/commercial-documents/components/doc-suggestive-list-id-filter.component';

import { ViewDropdownFilterComponent } from './filters/view-dropdown-filter.component';
import { ViewBooleanFilterComponent } from './filters/view-boolean-filter.component';
import { ViewOrderNumberFilterComponent } from './filters/view-order-number-filter.component';
import { SuggestiveListFilterComponent } from './filters/suggestive-list-filter.component';
import { ViewNumericFilterComponent } from './filters/view-numeric-filter.component';
import { ViewDateFilterComponent } from './filters/view-date-filter.component';
import { ViewFreeTextFilterComponent } from './filters/view-free-text-filter.component';
import { GlobalSearchFilterComponent } from './filters/global-search-filter.component';
import { SuggestiveListIdFilterComponent } from './filters/suggestive-list-id-filter.component';

@Component({
	selector: 'copo-datacenter-filter-customization',
	templateUrl: './datacenter-filter-customization.component.html',
	styleUrl: './datacenter-filter-customization.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		NgFor,
		NgIf,
		ViewFreeTextFilterComponent,
		GlobalSearchFilterComponent,
		ViewDateFilterComponent,
		DocumentDateFilterComponent,
		ViewNumericFilterComponent,
		SuggestiveListIdFilterComponent,
		SuggestiveListFilterComponent,
		DocumentSuggestiveListIdFilterComponent,
		ViewOrderNumberFilterComponent,
		DocumentOrderNumberFilterComponent,
		ViewBooleanFilterComponent,
		ViewDropdownFilterComponent,
		DocumentMillDropdownFilterComponent,
		DocumentLastConnectionDateComponent,
		MatIconModule,
		MatFormFieldModule,
		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatRippleModule,
		ShipmentOrderNumberFilterComponent,
		CdkScrollable
	]
})
export class DatacenterFilterCustomizationComponent implements OnInit, OnDestroy {
	@Input() allFilters!: FilterType[];
	@Input() addedFilters!: FilterType[];
	@Input() defaultFilters!: FilterType[];
	@ViewChildren('addedFilterHost', { read: ElementRef })
	private readonly addedFilterHosts?: QueryList<ElementRef<HTMLElement>>;

	searchText: string | undefined = '';
	searchTextControl = new FormControl('');
	filtersToShow: FilterType[] = [];
	booleanFilterToHide: string[] = [];
	private searchTextSubscription?: Subscription;
	private pendingFocusFilterId?: string;
	private focusAfterAddSubscription?: Subscription;

	constructor(private readonly ngZone: NgZone) {}

	ngOnInit() {
		this.filtersToShow = this.allFilters.toSorted(
			(f1, f2) => f1.label.localeCompare(f2.label)
		);

		for (const filterToAdd of this.addedFilters) {
			if (filterToAdd.filterType === 'Boolean') {
				this.booleanFilterToHide.push(filterToAdd.filterId);
				this.filtersToShow = this.filtersToShow.filter(filter => filter.filterId !== filterToAdd.filterId);
			}
		}
		const globalSearchFilter = this.filtersToShow.find(filter => filter.filterId === 'globalSearch');
		if (globalSearchFilter) {
			this.booleanFilterToHide.push(globalSearchFilter.filterId);
		}
		this.filtersToShow = this.filtersToShow.filter(filter => filter.filterId !== 'globalSearch');

		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged()
		).subscribe(value => {
			this.searchText = <string | undefined> value;
			if (this.searchText === undefined) {
				return;
			}

			this.filtersToShow = this.setFiltersToShow(
				this.allFilters.filter(filter => !this.booleanFilterToHide.includes(filter.filterId)),
				this.searchText
			);
		});
	}

	addValueFilter(filter: FilterType) {
		if (filter.filterType === 'Boolean' || filter.filterType === 'DocumentLastConnectionDate') {
			this.booleanFilterToHide.push(filter.filterId);
			this.filtersToShow = this.filtersToShow.filter(filterToHide => filterToHide.filterId !== filter.filterId);
		}
		const addedFilter = this.addFilter(filter);
		addedFilter.addEmptyValue();
		this.pendingFocusFilterId = addedFilter.filterId;
		this.focusAfterAddSubscription?.unsubscribe();
		this.focusAfterAddSubscription = this.ngZone.onStable.pipe(take(1)).subscribe(() => {
			this.focusPendingAddedFilter();
		});
	}

	removeFilter(filter: FilterType) {
		this.addedFilters.splice(this.addedFilters.indexOf(filter), 1);
		if (filter.filterType === 'Boolean' || filter.filterType === 'DocumentLastConnectionDate') {
			this.booleanFilterToHide = this.booleanFilterToHide.filter(filterId => filterId !== filter.filterId);
			this.filtersToShow.push(filter);
		}
	}

	setFiltersToShow(filters: FilterType[], searchText: string) {
		return filters.filter(filter => {
			if (filter.label) {
				return filter.label.replace(/\u00A0/g, ' ').toLowerCase()
					.search(searchText.toLowerCase()) >= 0;
			}
			return false;
		});
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
		if (this.focusAfterAddSubscription) {
			this.focusAfterAddSubscription.unsubscribe();
		}
	}

	private focusPendingAddedFilter() {
		const filterId = this.pendingFocusFilterId;
		if (!filterId || !this.addedFilterHosts) {
			return;
		}

		const host = this.addedFilterHosts
			.toArray()
			.map(ref => ref.nativeElement)
			.find(el => el.dataset['filterId'] === filterId);

		if (!host) {
			return;
		}

		const focusTarget = this.findFirstFocusableElement(host) ?? host;
		focusTarget.focus();
		this.pendingFocusFilterId = undefined;
	}

	private findFirstFocusableElement(container: HTMLElement): HTMLElement | null {
		const filterSelector = ['.filter-container'].join(',');
		const combinedSelector = [
			'input:not([disabled]):not([tabindex="-1"])',
			'[role="combobox"]',
			'.checkbox-button:not([disabled]):not([tabindex="-1"])',
		].join(',');
		const lastFilter = Array.from(container.querySelectorAll(filterSelector))
			.findLast((el): el is HTMLElement => el instanceof HTMLElement);

		const matches = lastFilter ? Array.from(lastFilter.querySelectorAll(combinedSelector))
			.filter((el): el is HTMLElement => el instanceof HTMLElement) : [];

		return matches.length > 0 ? matches[0] : null;
	}

	private addFilter(filter: FilterType) {
		let addedFilter = this.addedFilters.find(value => value.filterId === filter.filterId);
		if (!addedFilter) {
			addedFilter = filter;
			this.addedFilters.push(filter);
		}
		return addedFilter;
	}
}
